<template>
  <v-dialog v-model="dialog" width="900" :persistent="true">
    <template v-slot:activator="{ on }" v-if="addButtonEnabled">
      <permission-button
          :permission="canCreate"
          :tooltip="{right: true, bottom: false, attach: false}"
          @click="open"
          color="primary"
          class="ml-0 mr-0"
      >
        {{ 'app.menu_create' | trans }}
      </permission-button>
    </template>

  <v-stepper v-model="step">
    <v-stepper-header class="v-toolbar v-toolbar--card theme--dark primary">
      <v-stepper-step :complete="step > 1" step="1">{{ (!!currentEditedMenu ? 'app.menu.edit' : 'app.menu.create') | trans }}</v-stepper-step>
      <v-divider></v-divider>
      <v-stepper-step :complete="step > 2" step="2">{{ 'app.menu.distribution_of_macronutrients' | trans }}</v-stepper-step>
      <v-divider></v-divider>
      <v-stepper-step :complete="step > 3" step="3">{{ 'app.menu.demand_standards' | trans }}</v-stepper-step>
      <v-divider></v-divider>
      <v-stepper-step v-if="!currentEditedMenu" step="4">{{ 'app.menu.energy_distribution' | trans }}</v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <v-stepper-content step="1">
        <v-form ref="step1Form" v-model="validFirstStep" lazy-validation>
          <v-container grid-list-md >
            <v-layout wrap>
              <v-flex xs6>
                <v-menu
                    v-model="input"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                        v-model="date"
                        @blur="menu.start_date = parseDate(date)"
                        :label="'app.start_date' | trans"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        :rules="rules.start_date"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                      v-model="menu.start_date"
                      @input="input = false"
                      locale="pl-pl"
                      first-day-of-week="1">
                  </v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                    :label="trans('app.number_of_days_label', {
                      maxMenuDaysLength: maxMenuDaysLength
                    })"
                    v-model="menu.days"
                    full
                    :rules="rules.days"
                    type="number"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>

          <v-container grid-list-md >
            <v-layout wrap>
              <v-flex xs4></v-flex>
              <v-flex xs4 class="font-weight-bold text-left">Harris-Benedict</v-flex>
              <v-flex xs4 class="font-weight-bold text-left">Mifflin</v-flex>

              <v-flex xs4 class="font-weight-bold text-left">
                {{ 'app.menu.basic_metabolism' | trans }}
                <v-tooltip
                    top
                >
                  <template v-slot:activator="{ on }">
                    <v-icon small v-on="on">help</v-icon>
                  </template>
                  <span>{{ `app.interview.ppm_description` | trans }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs4 class="text-left">{{ patient.bmr_by_harris_benedict }} kcal</v-flex>
              <v-flex xs4 class="text-left">{{ patient.bmr_by_mifflin }} kcal</v-flex>

              <v-flex xs4 class="font-weight-bold text-left">
                {{ 'app.menu.full_metabolism' | trans }}
                <v-tooltip
                    top
                >
                  <template v-slot:activator="{ on }">
                    <v-icon small v-on="on">help</v-icon>
                  </template>
                  <span>{{ `app.interview.cpm_description` | trans }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs4 class="text-left">{{ patient.tmr_by_harris_benedict }} kcal</v-flex>
              <v-flex xs4 class="text-left">{{ patient.tmr_by_mifflin }} kcal</v-flex>

              <v-flex xs4 class="font-weight-bold text-left">{{ 'app.interview.current_weight' | trans }}</v-flex>
              <v-flex xs8 class="text-left">{{ patient.current_weight }} kg</v-flex>

              <v-flex xs4 class="font-weight-bold text-left">{{ 'app.interview.target_weight' | trans }}</v-flex>
              <v-flex xs8 class="text-left">{{ patient.target_weight }} kg</v-flex>

              <v-flex xs4 class="font-weight-bold text-left">{{ 'app.interview.fair_weight' | trans }}</v-flex>
              <v-flex xs8 class="text-left">{{ patient.fair_weight }} kg</v-flex>


              <v-flex xs4 class="font-weight-bold my-auto text-left">{{ 'app.menu.estimated_calorific_value' | trans }}</v-flex>
              <v-flex xs8>
                <v-text-field
                    :label="'app.menu.desired_diet_energy_modal' | trans"
                    v-model="menu.desired_diet_energy"
                    full
                    :rules="rules.desired_diet_energy"
                    suffix="kcal"
                    type="number"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
        <v-layout align-center justify-end row fill-height>
          <v-btn color="primary" flat @click="close">{{ 'app.close' | trans }}</v-btn>
          <v-btn color="primary" @click="goToStep(2)" :disabled="!validFirstStep">{{ 'app.next' | trans }}</v-btn>
        </v-layout>
      </v-stepper-content>
      <v-stepper-content step="2">
        <v-form ref="step2Form" v-model="validSecondStep" lazy-validation>
          <v-container grid-list-md >
            <v-layout row align-space-around wrap>
              <v-flex xs4 offset-xs1 d-flex class="font-weight-bold">
                <v-layout align-space-around justify-space-between column fill-height>
                  <v-flex d-flex></v-flex>
                  <v-flex d-flex align-end>
                    <v-flex no-padding class="text-left">
                      {{ 'app.menu.percentage_of_the_diet' | trans }}
                    </v-flex>
                  </v-flex>
                  <v-flex d-flex align-end>
                    <v-flex no-padding class="text-left">
                      {{ 'app.menu.quantity_in_grams' | trans }}
                    </v-flex>
                  </v-flex>
                </v-layout>
              </v-flex>

              <template v-for="nutrient in getBasicNutrients()">

                <v-flex xs2 d-flex>
                  <v-layout align-space-around justify-space-between column fill-height>
                    <v-flex d-flex class="font-weight-bold my-auto text-center">
                      {{ nutrient.name }}
                    </v-flex>
                    <v-flex d-flex>
                      <v-text-field text-xs-right
                          v-model="menu.nutrients[nutrient.id]"
                          full
                          :rules="rules.nutrient"
                          suffix="%"
                          @input="updateBasicNutrientsSum()"
                          type="number"
                          class="text-field-right my-0 py-0"
                      ></v-text-field>
                    </v-flex>
                    <v-flex class="text-xs-right">
                      {{ parseBasicNutrientPercentageValueToGrams(nutrient.id) }} g
                    </v-flex>
                  </v-layout>
                </v-flex>

              </template>

              <v-flex xs4 offset-xs1 class="font-weight-bold text-left">
                {{ 'app.menu.sum' | trans }}
              </v-flex>
              <v-flex xs6 class="text-left">
                {{ basicNutrientsSum }}%
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
        <v-layout align-center justify-end row fill-height>
          <v-btn @click="goToStep(1)">{{ 'app.previous' | trans }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="close">{{ 'app.close' | trans }}</v-btn>
          <v-btn color="primary" @click="goToStep(3)" :disabled="!validSecondStep || !isBasicSumPercentageCorrect">{{ 'app.next' | trans }}</v-btn>
        </v-layout>
      </v-stepper-content>
      <v-stepper-content step="3">
        <v-form ref="step3Form" v-model="validThirdStep" lazy-validation>
          <v-container grid-list-md >
            <v-layout wrap>
              <v-flex xs12 class="font-weight-bold text-left">
                <h3>{{ 'app.menu.other' | trans }}:</h3>
              </v-flex>
              <template v-for="nutrient in getOtherNutrients()">
                <v-flex xs4>
                  <v-text-field
                      v-model="menu.nutrients[nutrient.id]"
                      full
                      :rules="rules.nutrient"
                      :suffix="nutrient.unit"
                      :label="nutrient.name"
                      type="number"
                  ></v-text-field>
                </v-flex>
              </template>

              <v-flex xs12 class="font-weight-bold text-left">
                <h3>{{ 'app.menu.elements_norm' | trans }}:</h3>
              </v-flex>

              <template v-for="nutrient in getRestNutrients()">
                <v-flex xs2>
                  <v-text-field
                      v-model="menu.nutrients[nutrient.id]"
                      full
                      :rules="rules.nutrient"
                      :suffix="nutrient.unit"
                      :label="nutrient.name"
                      type="number"
                  ></v-text-field>
                </v-flex>
              </template>

            </v-layout>
          </v-container>
        </v-form>
        <v-layout align-center justify-end row fill-height>
          <v-btn @click="goToStep(2)">{{ 'app.previous' | trans }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="close">{{ 'app.close' | trans }}</v-btn>
          <v-btn v-if="!currentEditedMenu" color="primary" @click="goToStep(4)" :disabled="!validSecondStep || !isBasicSumPercentageCorrect">{{ 'app.next' | trans }}</v-btn>
          <v-btn v-else color="primary" @click="save()" :disabled="!validThirdStep || !isBasicSumPercentageCorrect">{{ 'app.save' | trans }}</v-btn>
        </v-layout>
      </v-stepper-content>
      <v-stepper-content v-if="!currentEditedMenu" step="4">
        <v-form lazy-validation v-model="validFourthStep" ref="step4Form" @input="validateStepFour">
          <v-container grid-list-md text-xs-center >
            <v-layout row wrap justify-center align-items-center>
              <v-flex xs12 md4 offset-md6 pt-0 pb-0>
                <v-radio-group v-model="inputMode" row>
                  <v-radio :label="`%`" value="percentage" :color="`success`"></v-radio>
                  <v-radio :class="`kcal-input-mode-radio`" :label="`app.energy` | trans" value="energy"
                           :color="`success`"></v-radio>
                </v-radio-group>
              </v-flex>
            </v-layout>
            <div v-for="(meal, index) in sortedSchema">
              <v-layout row wrap justify-center align-items-center>
                <v-flex xs12 md5 pt-0 pb-0>
                  <v-text-field
                      v-model="meal.name"
                      :label="trans('app.meal_name')"
                      :rules="rules.mealName"
                      required
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 md2 pt-0 pb-0>
                    <v-text-field
                        readonly
                        prepend-icon="access_time"
                        :label="'app.hour' | trans"
                        v-model="meal.time"
                        :rules="rules.time"
                        @click="openTimePicker(meal)"
                    ></v-text-field>
                </v-flex>
                <v-flex xs12 md2 pt-0 pb-0>
                  <v-text-field
                      :disabled="inputMode === 'energy'"
                      :value="meal.energy"
                      :label="trans('app.energy_percentage')"
                      suffix="%"
                      max="100"
                      required
                      step="1"
                      type="number"
                      :rules="rules.energy_percentage"
                      @input="calculateEnergy($event, index)"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 md2 pt-0 pb-0>
                  <v-text-field
                      :disabled="inputMode === 'percentage'"
                      :value="meal.kcal"
                      :label="trans('app.energy')"
                      required
                      suffix="kcal"
                      type="number"
                      :rules="[...rules.energy, validateDesiredEnergy(index)]"
                      @input="calculatePercentage($event, index)"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 md1 pt-0 pb-0>
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-btn
                          v-on="on"
                          flat
                          icon
                          color="red"
                          class="mr-0 ml-0"
                          @click="step4SchemaModel.splice(index, 1)"
                          :disabled="step4SchemaModel.length === 1"
                      >
                        <v-icon small>delete</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ 'app.delete' | trans }}</span>
                  </v-tooltip>
                </v-flex>
              </v-layout>
            </div>

            <v-divider></v-divider>

            <v-layout row>
              <v-flex xs5>
                <v-btn small flat color="success"
                       @click="addMeal()">
                  <v-icon dark>add</v-icon>
                  {{ 'app.add_meal' | trans }}
                </v-btn>
              </v-flex>
              <v-flex xs2 offset-xs2>
                <p class="text-xs-right">
                  {{ totalEnergy | round }} / 100 %
                </p>
              </v-flex>
              <v-flex xs2>
                <p class="text-xs-right">
                  {{ totalKcal | round }} / {{ this.menu.desired_diet_energy }} kcal
                </p>
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
        <v-dialog
            v-model="timePickerMenu"
            lazy
            hide-overlay
            full-width
            max-width="290px"
            min-width="290px"
        >
          <v-time-picker format="24hr" v-model="time" v-if="timePickerMenu"></v-time-picker>
        </v-dialog>

        <v-layout align-center justify-end row fill-height>
          <v-btn @click="goToStep(3)">{{ 'app.previous' | trans }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="close">{{ 'app.close' | trans }}</v-btn>
          <v-btn color="primary" @click="save()" :loading="loading" :disabled="!validFourthStep">{{ 'app.save' | trans }}</v-btn>
        </v-layout>

      </v-stepper-content>
    </v-stepper-items>

  </v-stepper>

  </v-dialog>
</template>

<style>
  .primary .v-stepper__step .v-stepper__label{
    color:#fff !important;
  }

  .primary .v-stepper__step--active .v-stepper__label{
    font-weight:700;
  }

  .v-stepper__step__step.primary{
    background-color:rgba(0,0,0,.38) !important;
    border-color:rgba(0,0,0,.38) !important;
  }
  .text-field-right input{
    text-align:right;
  }
</style>

<script>
import {mapActions, mapGetters} from "vuex";
import moment from 'moment';

import {trans} from "../../app";
import RulesBuilder from "../../utils/rulesBuilder";
import {BASIC_GROUP_IDS, CARBO, FATS, KCAL, OTHER_GROUP_IDS, PROTEIN} from "../../services/NutrientDetailsProvider";
import MenuService, {DEFAULT_DAYS_NUMBER} from "../../services/MenuService";
import {round} from "../../utils/money";
import {FORMAT_DATE, SYSTEM_DATE} from "../../utils/dateFormatter";
import NutrientsService from "../../services/NutrientsService";
import {DIETITIAN_FREE, DIETITIAN_TEST} from "../../utils/roles";

export default {
  data: function () {
    return {
      time: null,
      timePickerMenu: false,
      timeEditedMeal: null,
      dialog: false,
      step: 1,
      validFirstStep: false,
      validSecondStep: false,
      validThirdStep: false,
      validFourthStep: false,
      basicNutrientsSum: 0,
      loading: false,
      menu: {
        start_date: '',
        days: DEFAULT_DAYS_NUMBER,
        desired_diet_energy: null,
        nutrients: [],
      },
      rulesBuilder: new RulesBuilder(),
      date: '',
      input: false,
      patientNutrients: [],
      inputMode: 'energy',
      step4schema: [
        {
          name: 'Śniadanie',
          time: '08:00',
          energy: 25,
          kcal: 0,
        },
        {
          name: 'II Śniadanie',
          time: '10:00',
          energy: 10,
          kcal: 0,
        },
        {
          name: 'Obiad',
          time: '14:00',
          energy: 30,
          kcal: 0,
        },
        {
          name: 'Podwieczorek',
          time: '17:00',
          energy: 15,
          kcal: 0,
        },
        {
          name: 'Kolacja',
          time: '20:00',
          energy: 20,
          kcal: 0,
        },
      ],
      step4SchemaModel: [],
      nutrientsService: new NutrientsService(),
      menuNutrientsPrepared: false,
    };
  },
  props: {
    patientId: Number,
    addButtonEnabled: true,
    action: {
      type: String,
      required: false,
      default: null
    },
  },
  created() {
    this.fetchDietAssumptions();
    this.$root.$on('measurementStored', async () => {
      this.fetchDietAssumptions();
    });
    this.$root.$on('measurementDeleted', async () => {
      this.fetchDietAssumptions();
    });
    this.fetchPatient(this.patientId);
  },
  methods: {
    ...mapActions(['fetchMenus', 'setCurrentEditedMenu', 'fetchPatient']),
    open() {
      this.dialog = true;
      if (!this.menu.start_date) {
        this.menu.start_date = moment().format(SYSTEM_DATE);
      }
      if (!this.menu.days) {
        this.menu.days = DEFAULT_DAYS_NUMBER;
      }
      this.date = this.formatDate(this.menu.start_date);
    },
    async close() {
      if (this.action === 'go_to_list') {
        window.location.href = `/patients/${this.patientId}/menu`;
        return;
      }
      this.dialog = false;
      this.step = 1;
      this.$refs.step1Form.reset();
      await this.$nextTick();
      this.menu.days = DEFAULT_DAYS_NUMBER;
      this.menu.start_date = '';
      this.prepareMenuNutrients();
      this.step4SchemaModel = this.step4schema.map(obj => Object.assign({}, obj));
      this.setCurrentEditedMenu(null);
    },
    fetchDietAssumptions() {
      new MenuService().fetchDietAssumptions(this.patientId).then(({data}) => {
        this.patientNutrients = data.data;
        this.prepareMenuNutrients();
        this.step4SchemaModel = this.step4schema.map(obj => Object.assign({}, obj));
        this.updateBasicNutrientsSum();
      });
    },
    prepareMenuNutrients() {
      this.patientNutrients.forEach(nutrient => {
        this.menu.nutrients[nutrient.id] = round(nutrient.value,3);
      });
      this.menuNutrientsPrepared = true;
    },
    async save() {
      if (this.areFormsCorrect() === false) {
        return;
      }

      this.loading = true;
      let nutrients = this.menu.nutrients.reduce((accumulator, nutrientValue, nutrientId) => {
        if (!!nutrientValue) {
          accumulator[nutrientId] = nutrientValue
        }

        return accumulator;
      }, {});

      //set proper gram values for basic nutrients instead of percentage
      BASIC_GROUP_IDS.forEach((nutrientId) => {
        nutrients[nutrientId] = this.nutrientsService.parseBasicNutrientPercentageValueToGrams(
            this.menu.desired_diet_energy,
            nutrientId,
            +this.menu.nutrients[nutrientId],
        );
      });

      //set energy nutrient
      nutrients[KCAL] = Number(this.menu.desired_diet_energy);

      let nutrientsPercentage = {};
      nutrientsPercentage[PROTEIN] = String(this.menu.nutrients[PROTEIN]);
      nutrientsPercentage[FATS] = String(this.menu.nutrients[FATS]);
      nutrientsPercentage[CARBO] = String(this.menu.nutrients[CARBO]);
      if (this.currentEditedMenu) {
        try {
          await new MenuService().updateMenu(this.patientId, this.currentEditedMenu.id, {
            ...this.menu,
            nutrients,
            nutrients_percentage: nutrientsPercentage,
            edit_session_id: this.configuration.edit_session_id,
          });
        } catch (e) {
          if (e.response
              && e.response.status
              && +e.response.status === 409) {
            this.$root.$emit('menu_edit_conflict');
            return;
          }
          this.sentryCaptureException(e);
        }
        if (this.action === 'reload') {
          window.location.reload();
          return;
        }
        if (this.action === 'go_to_list') {
          window.location.href = `/patients/${this.patientId}/menu`;
          return;
        }
      } else {
        const {data} = await new MenuService().saveMenu(this.patientId, {
          ...this.menu,
          nutrients,
          meals: this.step4SchemaModel,
          nutrients_percentage: nutrientsPercentage,
        });

        window.location = `/menus/${data.data.uuid}`;
        return;
      }

      await this.fetchMenus({patientId: this.patientId});
      this.loading = false;
      this.close();
    },
    isOpen() {
      return this.dialog;
    },
    areFormsCorrect() {
      if (!this.currentEditedMenu) {
        return this.$refs.step1Form.validate() && this.$refs.step2Form.validate() && this.$refs.step3Form.validate() && this.$refs.step4Form.validate();
      } else {
        return this.$refs.step1Form.validate() && this.$refs.step2Form.validate() && this.$refs.step3Form.validate();
      }
    },
    formatDate(date) {
      if (!date) {
        return '';
      }
      return moment(date).format(FORMAT_DATE)
    },
    parseDate(date) {
      if (!date) {
        return '';
      }
      return moment(date, FORMAT_DATE).format(SYSTEM_DATE);
    },
    goToStep(step) {
      if ((step > this.step && this.$refs[`step${step - 1}Form`].validate()) || step < this.step) {
        this.step = step;
      }
      if (+step === 4) {
        this.recalculateEnergyAssigment();
      }
    },
    getBasicNutrients() {
      return this.patientNutrients.filter(nutrient => BASIC_GROUP_IDS.includes(nutrient.id))
          .map(number => this.mapToMaxThreeDigitsInDecimalPart(number));
    },
    getOtherNutrients() {
      return this.patientNutrients.filter(nutrient => OTHER_GROUP_IDS.includes(nutrient.id))
          .map(number => this.mapToMaxThreeDigitsInDecimalPart(number));
    },
    getRestNutrients() {
      return this.patientNutrients.filter(nutrient => [...OTHER_GROUP_IDS, ...BASIC_GROUP_IDS].includes(nutrient.id) === false)
          .map(number => this.mapToMaxThreeDigitsInDecimalPart(number));
    },
    updateBasicNutrientsSum() {
      this.basicNutrientsSum = BASIC_GROUP_IDS.reduce((accumulator, nutrientId) => accumulator + +this.menu.nutrients[nutrientId], 0);
    },
    validateStepFour() {
      if (this.$refs.step4Form !== undefined) {
        this.$refs.step4Form.validate();
      }
    },
    sortSchema() {
      this.step4SchemaModel.sort((a, b) => {
        if (!b.time) {
          return -1;
        }

        return moment(a.time, 'HH:mm').unix() - moment(b.time, 'HH:mm').unix();
      });
    },
    atLeastOneHundred() {
      return () => round(this.totalEnergy, 2) === 100 || trans('app.nutrients.validation.sum_has_to_equal', {attribute: '100%'});
    },
    calculateEnergy(percentage, index) {
      if (percentage < 0.01) {
        percentage = 0.01;
      }

      const value = round(percentage * this.menu.desired_diet_energy / 100, 0);

      this.step4SchemaModel[index].kcal = value;
      this.step4SchemaModel[index].energy = Number(percentage);
      if (this.totalEnergy === 100) {
        const difference = round(this.menu.desired_diet_energy - this.totalKcal, 0);
        const lastIndex = this.step4SchemaModel.length - 1;
        this.step4SchemaModel[lastIndex].kcal = round(this.step4SchemaModel[lastIndex].kcal + difference, 0);
      }

      return value;
    },
    validateDesiredEnergy(index) {
      const sum = this.step4SchemaModel.filter((v, i) => i !== index).map((v) => v.kcal).reduce((p, v) => p + v, 0);

      return (v) => sum + v === +this.menu.desired_diet_energy || trans('app.nutrients.validation.sum_has_to_equal', {attribute: `${this.menu.desired_diet_energy} kcal`});
    },
    calculatePercentage(kcal, index) {
      if (kcal < 1) {
        kcal = 1;
      }
      const value = round(kcal / this.menu.desired_diet_energy * 100, 2);

      this.step4SchemaModel[index].energy = value;
      this.step4SchemaModel[index].kcal = Number(kcal);
      if (this.totalKcal === Number(this.menu.desired_diet_energy)) {
        const difference = round(100 - round(this.totalEnergy, 2), 2);
        const lastIndex = this.step4SchemaModel.length - 1;
        this.step4SchemaModel[lastIndex].energy = round(this.step4SchemaModel[lastIndex].energy + difference, 2);
      }


      return value;
    },
    recalculateEnergyAssigment() {
      this.step4SchemaModel.forEach((s, i) => {
        this.calculateEnergy(s.energy, i);
      });

      const rest = this.menu.desired_diet_energy - this.totalKcal;

      if (rest > 0) {
        const indexOfMaxValue = this.step4SchemaModel.reduce((max, x, i, arr) => x > arr[max] ? i : max, 0);
        this.step4SchemaModel[indexOfMaxValue].kcal += rest;
      }
    },
    parseBasicNutrientPercentageValueToGrams(nutrientId) {
      return this.nutrientsService.parseBasicNutrientPercentageValueToGrams(
          this.menu.desired_diet_energy,
          nutrientId,
          +this.menu.nutrients[nutrientId]
      );
    },
    mapToMaxThreeDigitsInDecimalPart(number) {
      const stringNumber = String(number);
      const indexOfDot = stringNumber.indexOf('.');
      if (indexOfDot !== -1) {
        let decimalPart = stringNumber.slice(indexOfDot + 1, indexOfDot.length);
        if (decimalPart.length > 3) {
          decimalPart = decimalPart.slice(0, 3);
        }
        return +(stringNumber.slice(0, indexOfDot + 1) + decimalPart);
      }

      return number;
    },
    addMeal() {
      this.step4SchemaModel.push({ name: '', time: '', energy: null, kcal: null, })
      this.sortSchema();
    },
    openTimePicker(meal) {
      this.timePickerMenu = true;
      this.time = meal.time;
      this.timeEditedMeal = meal;
    },
    async prepareCurrentEditedMenu(menu) {
        if (!this.menuNutrientsPrepared) {
            await new Promise(r => setTimeout(r, 100));
            await this.prepareCurrentEditedMenu(menu);
            return;
        }
        if (menu !== null) {
            this.menu.start_date = menu.start_date;
            this.menu.days = menu.days;
            this.menu.desired_diet_energy = menu.desired_diet_energy;
            Object.values(menu.nutrients).forEach(nutrient => {
                if (!!nutrient.value) {
                    if (BASIC_GROUP_IDS.includes(nutrient.id)) {
                        this.menu.nutrients[nutrient.id] = this.nutrientsService.parseBasicNutrientGramsToPercentageValue(
                            menu.desired_diet_energy,
                            nutrient.id,
                            nutrient.value
                        );
                    } else {
                        this.menu.nutrients[nutrient.id] = nutrient.value;
                    }
                }
            });
            this.open();
        }
    },
    canCreate() {
        if ((this.userRole === DIETITIAN_FREE || this.userRole === DIETITIAN_TEST) && this.patientMenusCount >= 5) {
            return false;
        }
        return this.hasPermission('menu_schema_create');
    }
  },
  computed: {
    rules: function () {
      return {
        days: [
            ...this.rulesBuilder
                .addRequiredRule(trans('app.number_of_days'))
                .addIsIntegerRule(trans('app.number_of_days'))
                .addNumberBetweenRule(trans('app.number_of_days'), 1, this.maxMenuDaysLength)
                .getRules()
        ],
        start_date: [...this.rulesBuilder.addRequiredRule(trans('app.start_date')).getRules()],
        desired_diet_energy: [
            ...this.rulesBuilder
                .addRequiredRule(trans('app.menu.desired_diet_energy_modal'))
                .addIsIntegerRule(trans('app.menu.desired_diet_energy_modal'))
                .addNumberBiggerThanRule(trans('app.menu.desired_diet_energy_modal'), 100)
                .getRules()
        ],
        nutrient: [
            ...this.rulesBuilder
              .addRequiredRule('')
              .addIsNumericRule('')
              .addNumberBiggerThanRule('', 0)
              .getRules()
        ],
        mealName: [
            ...this.rulesBuilder
              .addRequiredRule(trans('app.meal_name'))
              .addMaxLengthRule(trans('app.meal_name'), 255)
              .getRules()
        ],
        time: [
            ...this.rulesBuilder.addTimeRule(trans('app.hour'))
            .getRules()
        ],
        energy_percentage: [
            ...this.rulesBuilder
                .addRequiredRule(trans('app.energy_percentage'))
                .getRules(),
            this.atLeastOneHundred(),
        ],
        energy: [
            ...this.rulesBuilder
                .addRequiredRule(trans('app.energy'))
                .getRules(),
        ]
      };
    },
    isBasicSumPercentageCorrect: function () {
      return this.basicNutrientsSum === 100;
    },
    sortedSchema() {
      if (this.step4SchemaModel !== undefined) {
        this.sortSchema();
        return this.step4SchemaModel;
      }
    },
    totalKcal() {
      if (this.step4SchemaModel) {
        return this.step4SchemaModel.map(s => s.kcal).reduce((p, c) => p + c, 0);
      }

      return 0;
    },
    totalEnergy() {
      if (this.step4SchemaModel) {
        return this.step4SchemaModel.map(s => s.energy).reduce((p, c) => p + c, 0);
      }

      return 0;
    },
    ...mapGetters([
        'editedMeasurement',
        'hasPermission',
        'patient',
        'currentEditedMenu',
        'configuration',
        'maxMenuDaysLength',
        'userRole',
        'patientMenusCount',
    ]),
  },
  watch: {
    'menu.start_date'(startDate) {
      this.date = startDate ? this.formatDate(startDate) : '';
    },
    currentEditedMenu(menu) {
      this.prepareCurrentEditedMenu(menu);
    },
    timePickerMenu(timePickerMenu) {
      if (!timePickerMenu && this.timeEditedMeal) {
        this.timeEditedMeal.time = this.time;
        this.time = null;
        this.timeEditedMeal = null;
        this.sortSchema();
      }
    }
  }
}
</script>
